import { Injectable } from '@angular/core';
import { StorageService } from './storage.service';
import { StartRegistrationRequest } from 'src/app/core/models/request/start-registration.request';
import { BaseRequest } from 'src/app/core/models/request/base.request';
import { OrganismModel } from 'src/app/core/models/organism.model';
import { BehaviorSubject, Observable } from 'rxjs';
import { User } from '../models/user';
import { Idle } from '@ng-idle/core';
import { RolesWeb } from '../enums/roles.enum';
import { Permissions } from '../enums/permissions.enum';
import { ILoginResponse } from '../models/response/login.response';
import { getNavigatorInfo } from '../utils/navigator.service';

@Injectable()
export class IdentityService {
  public static readonly AUTHORIZATION: string = 'Authorization';

  private readonly USER_KEY: string = 'user';
  public readonly APP_VERSION: string = require('package.json').version;
  public readonly APP_BUILD: string = '0';
  public readonly PLATFORM_CODE: string = '1';
  public readonly VERSION = getNavigatorInfo().version;

  private readonly STORAGE_USER_ID: string = 'userId';
  private readonly STORAGE_USER_URL: string = 'url';
  private readonly STORAGE_SESSION_TOKEN: string = 'token';
  private readonly STORAGE_USER: string = 'user';
  private readonly STORAGE_UUID: string = 'uuid';
  private readonly STORAGE_USER_EMAIL: string = 'account';
  private readonly STORAGE_PUSH_ENABLED: string = 'oneSignalEnabled';
  private readonly STORAGE_BALANCES: string = 'balances';
  private readonly STORAGE_BANK_INFO: string = 'bankInfo';
  private readonly STORAGE_CAPTCHA: string = 'tknCaptcha';
  private readonly BRANCHES: string = 'branches';
  private readonly APK_MESSAGES: string = 'apkMessage';
  private readonly AFIP: string = 'afipItem';

  private user: BehaviorSubject<User> = new BehaviorSubject(undefined);
  public currentUser: Observable<User> = this.user.asObservable();

  constructor(private storageService: StorageService, private idle: Idle) {}

  public saveUser(user: User, useSession = false) {
    this.getStorage(useSession).set(this.STORAGE_USER, JSON.stringify(user));
    this.user.next(user);
  }

  private getStorage(useSession: boolean = false) {
    return useSession ? this.storageService.fromSession : this.storageService;
  }

  public getUser(useSession: boolean = false): User | undefined {
    let response: User = undefined;
    if (this.getStorage(useSession).exists(this.USER_KEY)) {
      response = <User>this.getStorage(useSession).getAsObject(this.USER_KEY);
    }
    return response;
  }

  public getUserID(useSession: boolean = false): number | undefined {
    let user = this.getUser(useSession);
    if (user != undefined) {
      return user.id;
    }
    return undefined;
  }

  public getStoredUser(useSession: boolean = false): string | null {
    const account = this.getStorage(useSession).get(this.STORAGE_USER_EMAIL);
    return account ? account : null;
  }

  public setStoredUser(email: string, useSession: boolean = false): void {
    this.getStorage(useSession).set(this.STORAGE_USER_EMAIL, email);
  }

  public removeStoredUser(useSession: boolean = false): void {
    const userData = this.getStoredUser(useSession);
    if (userData) {
      this.getStorage(useSession).removeItem(this.STORAGE_USER_EMAIL);
    }
  }

  public getUserValidity(useSession: boolean = false): boolean {
    const user = this.getUser(useSession);
    let response = false;
    if (user != undefined) {
      response = user.statusRegistro.completo;
    }
    return response;
  }

  public isCommerce(): boolean {
    const user = this.getUser();
    let response = false;
    if (user != undefined) {
      response = user.organismo != null;
    }
    return response;
  }

  public isRolResponsible(): boolean {
    return this.hasRolId(RolesWeb.RESPONSABLE);
  }

  public commerceIsComplete(): boolean {
    const user = this.getUser();
    let response = false;
    if (user != undefined && user.organismo) {
      response = user.organismo.rubro != null;
    }
    return response;
  }

  public getRegistrationRequest(request: any): StartRegistrationRequest {
    request.versionAplicativo = this.getYacareVersion();
    request.plataforma = this.PLATFORM_CODE;
    request.version = this.getYacareVersion();
    request.descripcion = 'Pix-Web';
    request.idUsuario = 0;
    request.uuid = this.getUUID();
    return request;
  }

  public getRequest<T extends BaseRequest>(request: T): T {
    request.idUsuario = this.getUserID();
    request.uuid = this.getUUID();
    request.versionAplicativo = this.getYacareVersion();
    return request;
  }

  public getYacareVersion(): string {
    return this.APP_VERSION;
  }

  public getYacareBuild(): string {
    return this.APP_BUILD;
  }

  public setUUID(uuid: string) {
    this.storageService.set(this.STORAGE_UUID, uuid);
  }

  public getUUID(): string {
    if (this.storageService.exists(this.STORAGE_UUID)) {
      return this.storageService.get(this.STORAGE_UUID);
    } else {
      return null;
    }
  }

  public setPushEnabled(isEnabled: boolean) {
    this.storageService.setObject(this.STORAGE_PUSH_ENABLED, isEnabled);
  }

  public isPushEnabled(): boolean {
    return this.storageService.exists(this.STORAGE_PUSH_ENABLED)
      ? this.storageService.getAsObject(this.STORAGE_PUSH_ENABLED)
      : false;
  }

  public clearSession(useSession = false) {
    this.getStorage(useSession).removeItem(this.STORAGE_SESSION_TOKEN);
    this.getStorage(useSession).removeItem(this.STORAGE_USER);
    this.getStorage(useSession).removeItem(this.STORAGE_USER_ID);
    this.getStorage(useSession).removeItem(this.STORAGE_USER_URL);
    this.getStorage(useSession).removeItem(this.STORAGE_BALANCES);
    this.getStorage(useSession).removeItem(this.STORAGE_BANK_INFO);
    this.getStorage(useSession).removeItem(this.STORAGE_CAPTCHA);
    this.getStorage(useSession).removeItem(this.BRANCHES);
    this.getStorage(useSession).removeItem(this.APK_MESSAGES);
    sessionStorage.removeItem(this.STORAGE_UUID);
    sessionStorage.clear();
    this.idle.stop();
  }

  public saveSession(user: ILoginResponse, useSession = false) {
    this.getStorage(useSession).set(this.STORAGE_USER_ID, user.user.id.toString());
    this.getStorage(useSession).set(this.STORAGE_SESSION_TOKEN, user.user.token);
    this.saveUser(user.user, useSession);
  }

  public saveSessionUser(user: User, useSession = false) {
    this.getStorage(useSession).set(this.STORAGE_USER_ID, user.id.toString());
    this.getStorage(useSession).set(this.STORAGE_SESSION_TOKEN, user.token);
    this.saveUser(user, useSession);
  }

  public getUserOrganism(): OrganismModel {
    let user = this.getUser();
    if (user != undefined) {
      return user.organismo;
    }

    return undefined;
  }

  /**
   * @deprecated
   */
  public updateUserValidate(estado: boolean): void {
    var user = this.getUser();
    if (user != undefined) {
      user.userValidate = estado;
      this.saveUser(user);
    }
  }

  public updateUserOrganism(organism: OrganismModel): void {
    let user = this.getUser();
    if (user != undefined) {
      user.organismo = organism;
      this.saveUser(user);
    }
  }

  public getToken(): string | undefined {
    let token: string = undefined;
    let sessionUsage = [false, true];

    sessionUsage.forEach((useSession) => {
      if (this.getStorage(useSession).exists(this.STORAGE_SESSION_TOKEN)) {
        token = this.getStorage(useSession).get(this.STORAGE_SESSION_TOKEN);
      }
    });

    return token;
  }

  public setToken(token: string, useSession = false): void {
    this.getStorage(useSession).set(this.STORAGE_SESSION_TOKEN, token);
  }

  getPermissions(): Permissions[] {
    const user = this.getUser();
    return user?.permisos ?? [];
  }

  hasRolId(rolId: number): boolean {
    const user = this.getUser();
    return user?.rol?.id === rolId;
  }

  hasPermission(permission: Permissions): boolean {
    return this.getPermissions().includes(permission);
  }
}
